import { EditOutlined } from '@ant-design/icons';
import { App, Modal } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { get } from 'lodash-es';
import { memo, useEffect, useState } from 'react';

import { FooterWrapper, Wrapper } from '@page/webhooks/new-webhook/new-webhook.style';
import { webhooksActions } from '@store/webhooks/webhooks.slice';

import { Button } from '@atom/button/button';
import { ButtonTypeEnum } from '@atom/button/button.enum';
import { Title } from '@atom/typography/typography';
import { NewWebhookForm } from '@form/new-webhook/new-webhook.form';
import { useMutation } from '@hook/common/use-mutation.hook';
import { useStoreSelector } from '@hook/store/store.hooks';
import { useUpdateWebhookMutation } from '@hook/webhook/webhook.hooks';
import { ErrorFormInterface } from '@interface/form/error-form.interface';
import { WebhookStatusEnum } from '@interface/webhook/webhook.interface';

interface DeleteWebhookProps {
  id: string;
}

export const EditWebhook = memo(({ id }: DeleteWebhookProps) => {
  const { data, isLoading } = useStoreSelector((state) => state.webhooks);
  const [form] = useForm();
  const [isOpen, setOpen] = useState(false);
  const { action, state } = useMutation(useUpdateWebhookMutation(), webhooksActions, webhooksActions.setWebhook);
  const { message } = App.useApp();

  useEffect(() => void (isOpen && form.setFieldsValue(get(data, id))), [isOpen]);

  const onFinish = (values: any) =>
    void action({ ...values, id, status: values.status ? WebhookStatusEnum.Enabled : WebhookStatusEnum.Disabled });

  useEffect(() => {
    if (state.isSuccess) {
      setOpen(false);
      message.success('Webhook was successfully updated');
      form.resetFields();
    }
  }, [state.isSuccess]);

  return (
    <>
      <Modal
        confirmLoading={isLoading || state.isLoading}
        open={isOpen}
        width={640}
        footer={
          <FooterWrapper>
            <Button key='back' type={ButtonTypeEnum.Primary} ghost onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button key='submit' type={ButtonTypeEnum.Primary} onClick={() => form.validateFields().then(onFinish)}>
              Update webhook
            </Button>
          </FooterWrapper>
        }
        title={
          <Title level={4} weight={400}>
            Update webhook
          </Title>
        }
        onCancel={() => setOpen(false)}
        onOk={() => form.validateFields().then(onFinish)}
      >
        <Wrapper>
          <NewWebhookForm error={state.error as ErrorFormInterface} form={form} />
        </Wrapper>
      </Modal>
      <Button title='Edit Webhook' type={ButtonTypeEnum.Default} onClick={() => setOpen(true)}>
        <EditOutlined />
      </Button>
    </>
  );
});
